/**
 * Shadow DOM Content Detection
 * 
 * Enhanced content detection that works from within Shadow DOM environments.
 * This detector can access the host document even when called from within 
 * a Shadow DOM boundary, ensuring content detection continues to work properly.
 */

import { shadowLogger } from '../utils/logger'
import type { DetectedContent } from '../embed/shared/types'

/**
 * Shadow DOM-aware content detector
 * Extends the base content detection to work properly from Shadow DOM context
 */
export class ShadowContentDetector {
  /**
   * Get the host document, even from within Shadow DOM
   */
  private static getHostDocument(): Document {
    // Access the main document, even from Shadow DOM context
    return window.top?.document || document
  }

  /**
   * Detect page title from host document
   */
  static detectTitle(selectors?: string[]): string {
    const hostDoc = this.getHostDocument()
    
    const defaultSelectors = [
      'h1.article-title',
      'h1[class*="title"]',
      'h1[class*="headline"]',
      '.entry-title h1',
      '.post-title h1',
      'article h1',
      'h1'
    ]
    
    const selectorsToUse = selectors || defaultSelectors
    
    shadowLogger.debug('Detecting title from host document', { 
      selectors: selectorsToUse,
      isHostDoc: hostDoc !== document 
    })
    
    for (const selector of selectorsToUse) {
      try {
        const element = hostDoc.querySelector(selector)
        if (element?.textContent?.trim()) {
          const title = element.textContent.trim()
          shadowLogger.debug('✅ Title detected', { selector, title: title.substring(0, 100) })
          return title
        }
      } catch (error) {
        shadowLogger.warn(`Failed to query selector: ${selector}`, error)
      }
    }
    
    // Fallback to document title
    const fallbackTitle = hostDoc.title
    shadowLogger.debug('Using fallback title', { title: fallbackTitle })
    return fallbackTitle
  }

  /**
   * Detect page content from host document
   */
  static detectContent(selectors?: string[]): string {
    const hostDoc = this.getHostDocument()
    
    const defaultSelectors = [
      '#ArticleBodyComponent',
      '.ArticleBodyComponent',
      '.text-content',
      '.article-body',
      '.entry-content',
      '.post-content',
      '.content',
      'article .content',
      'main article',
      '[role="main"] article',
      '.prose',
      // Draft.js and CMS specific selectors
      '.public-DraftEditor-content',
      '.DraftEditor-root',
      '.text_editor_content',
      '.editor-content',
      // WordPress and other CMS selectors
      '.wp-block-post-content',
      '.entry-content',
      '.post-body',
      '.article-content'
    ]
    
    const selectorsToUse = selectors || defaultSelectors
    
    shadowLogger.debug('Detecting content from host document', { 
      selectors: selectorsToUse,
      isHostDoc: hostDoc !== document 
    })
    
    for (const selector of selectorsToUse) {
      try {
        const element = hostDoc.querySelector(selector)
        if (element) {
          let content = ''
          
          // Special handling for Draft.js content
          if (selector.includes('DraftEditor') || element.classList.contains('public-DraftEditor-content')) {
            content = this.extractDraftJsContent(element)
          } else {
            content = this.extractCleanContent(element)
          }
          
          if (content && content.length > 100) {
            shadowLogger.debug('✅ Content detected', { 
              selector, 
              length: content.length,
              preview: content.substring(0, 200),
              isDraftJs: selector.includes('DraftEditor')
            })
            return content
          }
        }
      } catch (error) {
        shadowLogger.warn(`Failed to query selector: ${selector}`, error)
      }
    }
    
    // Fallback: try Draft.js content detection first, then p tags
    try {
      // Try to find Draft.js content as fallback
      const draftContent = hostDoc.querySelector('.public-DraftEditor-content')
      if (draftContent) {
        const extractedContent = this.extractDraftJsContent(draftContent)
        if (extractedContent.length > 100) {
          shadowLogger.debug('Using fallback Draft.js content', { 
            contentLength: extractedContent.length 
          })
          return extractedContent
        }
      }
      
      // Final fallback: get all p tags content from host document
      const paragraphs = Array.from(hostDoc.querySelectorAll('p'))
        .map(p => p.textContent?.trim() || '')
        .filter(text => text.length > 20 && this.isLikelyContentText(text))
      
      const fallbackContent = paragraphs.join(' ').slice(0, 5000)
      shadowLogger.debug('Using fallback content from paragraphs', { 
        paragraphCount: paragraphs.length,
        contentLength: fallbackContent.length 
      })
      
      return fallbackContent
    } catch (error) {
      shadowLogger.error('Failed to extract fallback content', error)
      return ''
    }
  }

  /**
   * Extract content from Draft.js editor structures
   * Specifically handles the nested structure with data-text="true" elements
   */
  private static extractDraftJsContent(element: Element): string {
    shadowLogger.debug('Extracting Draft.js content', { elementClass: element.className })
    
    // Look for text paragraphs within Draft.js structure
    const textElements = element.querySelectorAll('[data-text="true"]')
    const paragraphs: string[] = []
    
    textElements.forEach(textEl => {
      const text = textEl.textContent?.trim()
      if (text && text.length > 10) {
        // Filter out common non-content elements
        const parentBlock = textEl.closest('[data-block="true"]')
        const isContentParagraph = parentBlock?.classList.contains('text_editor_paragraph')
        
        // Skip if it's likely navigation, ads, or metadata
        if (this.isLikelyContentText(text) && isContentParagraph) {
          paragraphs.push(text)
        }
      }
    })
    
    const content = paragraphs.join('\n\n')
    shadowLogger.debug('Draft.js content extracted', {
      textElementsFound: textElements.length,
      paragraphsExtracted: paragraphs.length,
      contentLength: content.length
    })
    
    return content
  }

  /**
   * Extract clean content from regular HTML elements
   * Filters out navigation, ads, and other non-content elements
   */
  private static extractCleanContent(element: Element): string {
    const content = element.textContent?.trim() || ''
    
    // Basic filtering for obviously non-content elements
    if (content.length < 50) return ''
    
    // Filter out elements that are likely not main content
    const skipSelectors = [
      '.advertisement', '.ad', '.ads',
      '.navigation', '.nav', '.menu',
      '.sidebar', '.footer', '.header',
      '.social-share', '.comments',
      '.related-articles', '.recommendations'
    ]
    
    for (const skipSelector of skipSelectors) {
      if (element.matches(skipSelector) || element.querySelector(skipSelector)) {
        // If the element contains significant ad/nav content, try to filter it out
        const cleanContent = this.filterNonContentText(content)
        if (cleanContent.length > 100) {
          return cleanContent
        }
      }
    }
    
    return content
  }

  /**
   * Check if text is likely to be article content vs navigation/ads
   */
  private static isLikelyContentText(text: string): boolean {
    if (text.length < 20) return false
    
    // Skip common navigation/UI text patterns
    const skipPatterns = [
      /^צפייה בגלריה/,
      /^הצטרפו לערוץ/,
      /^שתפו/,
      /^לַחֲלוֹק/,
      /^רק מה שמעניין/,
      /^\d+\s*צפייה/,
      /^(פייסבוק|טוויטר|לינקדאין)/i,
      /^(share|follow|subscribe)/i
    ]
    
    return !skipPatterns.some(pattern => pattern.test(text))
  }

  /**
   * Filter out non-content text from mixed content
   */
  private static filterNonContentText(text: string): string {
    const lines = text.split('\n').map(line => line.trim()).filter(Boolean)
    const contentLines = lines.filter(line => this.isLikelyContentText(line))
    return contentLines.join('\n')
  }

  /**
   * Detect author information from host document
   */
  static detectAuthor(selectors?: string[]): string | null {
    const hostDoc = this.getHostDocument()
    
    const defaultSelectors = [
      '.author-name',
      '.byline',
      '[class*="author"]',
      '[rel="author"]',
      '.post-author'
    ]
    
    const selectorsToUse = selectors || defaultSelectors
    
    shadowLogger.debug('Detecting author from host document', { 
      selectors: selectorsToUse,
      isHostDoc: hostDoc !== document 
    })
    
    for (const selector of selectorsToUse) {
      try {
        const element = hostDoc.querySelector(selector)
        if (element?.textContent?.trim()) {
          const author = element.textContent.trim()
          shadowLogger.debug('✅ Author detected', { selector, author })
          return author
        }
      } catch (error) {
        shadowLogger.warn(`Failed to query selector: ${selector}`, error)
      }
    }
    
    shadowLogger.debug('No author information found')
    return null
  }

  /**
   * Get current URL (same as host document)
   */
  static detectUrl(): string {
    const url = window.location.href
    shadowLogger.debug('Current URL detected', { url })
    return url
  }

  /**
   * Detect all content from host document
   * This method is called from within Shadow DOM but accesses host page content
   */
  static detectAll(contentSelectors?: {
    title?: string
    content?: string
    author?: string
  }): DetectedContent {
    shadowLogger.info('Starting comprehensive content detection from Shadow DOM...')
    
    try {
      const detected = {
        title: this.detectTitle(contentSelectors?.title ? [contentSelectors.title] : undefined),
        content: this.detectContent(contentSelectors?.content ? [contentSelectors.content] : undefined),
        author: this.detectAuthor(contentSelectors?.author ? [contentSelectors.author] : undefined),
        url: this.detectUrl()
      }

      shadowLogger.info('✅ Content detection completed', {
        hasTitle: !!detected.title,
        hasContent: !!detected.content && detected.content.length > 0,
        hasAuthor: !!detected.author,
        contentLength: detected.content?.length || 0,
        titlePreview: detected.title?.substring(0, 50) || 'No title'
      })

      return detected
      
    } catch (error) {
      shadowLogger.error('❌ Content detection failed', error)
      
      // Return minimal fallback data
      return {
        title: this.getHostDocument().title || 'Untitled',
        content: '',
        author: null,
        url: this.detectUrl()
      }
    }
  }

  /**
   * Test content detection capabilities
   * Useful for debugging content detection issues
   */
  static testDetection(): void {
    shadowLogger.info('🧪 Testing Shadow DOM content detection...')
    
    const hostDoc = this.getHostDocument()
    const testResults = {
      hostDocumentAccess: hostDoc !== document,
      hostDocumentTitle: hostDoc.title,
      availableHeadings: hostDoc.querySelectorAll('h1, h2, h3').length,
      availableParagraphs: hostDoc.querySelectorAll('p').length,
      availableArticles: hostDoc.querySelectorAll('article').length,
      // Draft.js specific detection
      hasDraftJsContent: !!hostDoc.querySelector('.public-DraftEditor-content'),
      draftJsTextElements: hostDoc.querySelectorAll('[data-text="true"]').length,
      draftJsParagraphs: hostDoc.querySelectorAll('.text_editor_paragraph').length,
      canDetectContent: false
    }

    try {
      const detected = this.detectAll()
      testResults.canDetectContent = !!(detected.title || detected.content)
      
      shadowLogger.info('✅ Content detection test completed', testResults)
    } catch (error) {
      shadowLogger.error('❌ Content detection test failed', { ...testResults, error })
    }
  }

  /**
   * Watch for content changes in host document
   * Useful for SPAs where content changes dynamically
   */
  static watchForContentChanges(
    callback: (content: DetectedContent) => void,
    options: { debounceMs?: number } = {}
  ): () => void {
    const { debounceMs = 1000 } = options
    let timeoutId: NodeJS.Timeout | null = null
    
    const handleChange = () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      
      timeoutId = setTimeout(() => {
        try {
          const newContent = this.detectAll()
          callback(newContent)
          shadowLogger.debug('Content change detected and callback executed')
        } catch (error) {
          shadowLogger.error('Error in content change callback', error)
        }
      }, debounceMs)
    }

    // Watch for DOM changes in host document
    const hostDoc = this.getHostDocument()
    const observer = new MutationObserver(handleChange)
    
    observer.observe(hostDoc.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: false // Avoid excessive callbacks
    })

    shadowLogger.info('✅ Content change watcher established', { debounceMs })

    // Return cleanup function
    return () => {
      observer.disconnect()
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      shadowLogger.info('Content change watcher cleaned up')
    }
  }
}